<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import {
  patientTasksApi,
  type PatientTaskForm,
  type PatientTask,
  type TimeType,
} from '@/api/patient-tasks';
import type { TaskDefinition } from '@/api/task-definitions';
import { taskCategoriesApi } from '@/api/task-categories';
import TimeInput from '@/components/time-input.vue';

interface Props {
  patientId: number;
  taskDefinitions: TaskDefinition[];
  patientTask?: PatientTask | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'success'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const queryClient = useQueryClient();

const isEditing = computed(() => !!props.patientTask);
const selectedCategoryId = ref<number | null>(null);

function formatTimeForInput(timeString: string | undefined): string {
  if (!timeString) return '';
  if (/^\d{2}:\d{2}$/.test(timeString)) return timeString;

  try {
    const date = new Date(timeString);
    const timeFormatLength = 5;

    return date.toTimeString().slice(0, timeFormatLength);
  } catch {
    return '';
  }
}

function getInitialTimeType(): TimeType {
  if (!props.patientTask) return 'specific';

  return props.patientTask.timeType || 'specific';
}

function getInitialScheduledTime(): string {
  if (!props.patientTask) return '';
  if (props.patientTask.timeType !== 'specific') return '';

  return formatTimeForInput(
    props.patientTask.humanScheduledTime || props.patientTask.scheduledTime,
  );
}

const { data: taskCategories } = useQuery({
  queryKey: ['taskCategories', 'active'],
  queryFn: () => taskCategoriesApi.index('active'),
  initialData: [],
});

const validationSchema = yup.object({
  taskDefinitionId: yup
    .number()
    .required('La tarea es requerida')
    .min(1, 'La tarea es requerida'),
  timeType: yup
    .string()
    .required('El tipo de horario es requerido')
    .oneOf(['specific', 'am', 'pm']),
  scheduledTime: yup
    .string()
    .when('timeType', {
      is: 'specific',
      then: schema => schema
        .required('La hora es requerida')
        .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Formato de hora inválido'),
      otherwise: schema => schema.optional(),
    }),
  observations: yup.string().optional(),
});

const { values, errors, defineField, handleSubmit, meta, resetField } = useForm<PatientTaskForm>({
  validationSchema,
  initialValues: {
    taskDefinitionId: props.patientTask?.taskDefinitionId,
    timeType: getInitialTimeType(),
    scheduledTime: getInitialScheduledTime(),
    observations: props.patientTask?.observations || '',
  },
});

const [taskDefinitionId, taskDefinitionIdAttrs] = defineField('taskDefinitionId');
const [timeType, timeTypeAttrs] = defineField('timeType');
const [scheduledTime] = defineField('scheduledTime');
const [observations, observationsAttrs] = defineField('observations');

const timeTypeOptions = [
  { value: 'specific', label: 'Hora específica' },
  { value: 'am', label: 'Mañana (AM)' },
  { value: 'pm', label: 'Tarde (PM)' },
];

const isSpecificTime = computed(() => timeType.value === 'specific');

const createMutation = useMutation({
  mutationFn: () => patientTasksApi.create(props.patientId, values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['patientTasks', props.patientId] });
    emit('success');
  },
});

const updateMutation = useMutation({
  mutationFn: () => patientTasksApi.update(props.patientTask!.id, values),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['patientTasks'] });
    emit('success');
  },
});

const isLoading = computed(() => createMutation.isPending.value || updateMutation.isPending.value);
const hasError = computed(() => createMutation.isError.value || updateMutation.isError.value);

const onSubmit = handleSubmit(() => {
  if (isEditing.value) {
    updateMutation.mutate();
  } else {
    createMutation.mutate();
  }
});

function handleClose() {
  emit('close');
}

const activeTaskDefinitions = computed(() => {
  const active = props.taskDefinitions.filter(taskDefinition => !taskDefinition.discarded);
  if (!selectedCategoryId.value) {
    return active;
  }

  return active.filter(taskDefinition => taskDefinition.taskCategoryId === selectedCategoryId.value);
});

function clearCategoryFilter() {
  selectedCategoryId.value = null;
}

watch(selectedCategoryId, (newCategoryId) => {
  if (taskDefinitionId.value && newCategoryId) {
    const selectedTask = props.taskDefinitions.find(
      taskDefinition => taskDefinition.id === taskDefinitionId.value,
    );
    if (selectedTask && selectedTask.taskCategoryId !== newCategoryId) {
      resetField('taskDefinitionId', { value: null });
    }
  }
});

const formTitle = computed(() => (isEditing.value ? 'Editar Tarea' : 'Asignar Tarea'));
const submitButtonText = computed(() => (isEditing.value ? 'Actualizar Tarea' : 'Asignar Tarea'));
</script>
<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center">
      <span>{{ formTitle }}</span>
      <v-btn
        icon="mdi-close"
        variant="text"
        size="small"
        :disabled="isLoading"
        @click="handleClose"
      />
    </v-card-title>
    <v-card-text>
      <form @submit.prevent="onSubmit">
        <v-select
          v-model="selectedCategoryId"
          :items="taskCategories"
          item-title="name"
          item-value="id"
          label="Filtrar tareas por categoría (opcional)"
          variant="outlined"
          class="mb-4"
          clearable
          @click:clear="clearCategoryFilter"
        >
          <template #prepend-inner>
            <v-icon icon="mdi-filter" />
          </template>
        </v-select>
        <v-autocomplete
          v-model="taskDefinitionId"
          :items="activeTaskDefinitions"
          item-title="name"
          item-value="id"
          label="Tarea a asignar"
          :error-messages="errors.taskDefinitionId"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          v-bind="taskDefinitionIdAttrs"
        >
          <template #prepend-inner>
            <v-icon icon="mdi-clipboard-list" />
          </template>
        </v-autocomplete>

        <v-radio-group
          v-model="timeType"
          label="Tipo de horario"
          :disabled="isLoading"
          inline
          class="mb-4"
          v-bind="timeTypeAttrs"
        >
          <v-radio
            v-for="option in timeTypeOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </v-radio-group>

        <time-input
          v-if="isSpecificTime"
          v-model="scheduledTime"
          label="Hora programada"
          :error-messages="errors.scheduledTime"
          :disabled="isLoading"
          class="mb-4"
        >
          <template #prepend-inner>
            <v-icon icon="mdi-clock-outline" />
          </template>
        </time-input>

        <v-alert
          v-else
          type="info"
          variant="tonal"
          class="mb-4"
        >
          <template v-if="timeType === 'am'">
            La tarea se asignará para realizar en la mañana (00:00 - 11:59)
          </template>
          <template v-else>
            La tarea se asignará para realizar en la tarde (12:00 - 23:59)
          </template>
        </v-alert>

        <v-textarea
          v-model="observations"
          label="Observaciones (opcional)"
          :error-messages="errors.observations"
          :disabled="isLoading"
          variant="outlined"
          class="mb-4"
          rows="3"
          auto-grow
          v-bind="observationsAttrs"
        >
          <template #prepend-inner>
            <v-icon icon="mdi-note-text-outline" />
          </template>
        </v-textarea>
      </form>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        :disabled="isLoading"
        @click="handleClose"
      >
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!meta.valid"
        :loading="isLoading"
        @click="onSubmit"
      >
        {{ submitButtonText }}
      </v-btn>
    </v-card-actions>

    <v-alert
      v-if="hasError"
      type="error"
      variant="tonal"
      class="mx-4 mb-4"
    >
      Ha ocurrido un error al {{ isEditing ? 'actualizar' : 'asignar' }} la tarea. Intenta nuevamente.
      Recuerda que una misma tarea no puede ser asignada a un paciente en la misma hora.
    </v-alert>
  </v-card>
</template>
